import type { ErrorReason } from './protocol'

// Explicit rendering with execution: 'execute' — the widget is rendered once
// but the challenge only runs when execute() is called, so tokens are minted
// on demand at payment submit (they are single-use and expire after 300s).
// https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/

const API_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__xceedTurnstileLoaded'

interface TurnstileApi {
  render(container: HTMLElement, params: Record<string, unknown>): string | undefined
  execute(container: HTMLElement): void
  reset(widgetId: string): void
  remove(widgetId: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    __xceedTurnstileLoaded?: () => void
  }
}

export class CaptchaError extends Error {
  constructor(public reason: ErrorReason) {
    super(`captcha failed: ${reason}`)
  }
}

let apiPromise: Promise<TurnstileApi> | null = null

function loadApi(): Promise<TurnstileApi> {
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      window.__xceedTurnstileLoaded = () => {
        delete window.__xceedTurnstileLoaded
        resolve(window.turnstile!)
      }
      const script = document.createElement('script')
      script.src = API_URL
      script.async = true
      script.onerror = () => {
        apiPromise = null
        reject(new CaptchaError('turnstile-error'))
      }
      document.head.appendChild(script)
    })
  }
  return apiPromise
}

interface PendingExecution {
  resolve: (token: string) => void
  reject: (error: CaptchaError) => void
}

export class TurnstileExecutor {
  private widgetId: string | null = null
  private renderedAction: string | null = null
  private pending: PendingExecution | null = null
  private chain: Promise<unknown> = Promise.resolve()

  constructor(
    private container: HTMLElement,
    private sitekey: string,
  ) {}

  // Executions are serialized: the parent guards against double-submit, but a
  // retry can arrive while a previous challenge is settling.
  getToken(action: string): Promise<string> {
    const result = this.chain
      .catch(() => undefined)
      .then(() => this.executeOnce(action))
    this.chain = result.catch(() => undefined)
    return result
  }

  private async executeOnce(action: string): Promise<string> {
    if (!this.sitekey) throw new CaptchaError('config-error')
    const api = await loadApi()
    this.ensureRendered(api, action)

    try {
      return await new Promise<string>((resolve, reject) => {
        this.pending = { resolve, reject }
        api.execute(this.container)
      })
    } finally {
      this.pending = null
      // Tokens are single-use: reset so the next execute() mints a fresh one.
      if (this.widgetId !== null) api.reset(this.widgetId)
    }
  }

  // `action` is a render-time parameter. It is constant for a given checkout
  // session, so re-rendering on change is a safety net, not a hot path.
  private ensureRendered(api: TurnstileApi, action: string): void {
    if (this.widgetId !== null && this.renderedAction === action) return
    if (this.widgetId !== null) {
      api.remove(this.widgetId)
      this.widgetId = null
    }

    const widgetId = api.render(this.container, {
      sitekey: this.sitekey,
      action,
      execution: 'execute',
      callback: (token: string) => this.pending?.resolve(token),
      'error-callback': () => {
        this.pending?.reject(new CaptchaError('turnstile-error'))
        return true // suppress Turnstile's own error logging
      },
      'expired-callback': () => this.pending?.reject(new CaptchaError('expired')),
      'timeout-callback': () => this.pending?.reject(new CaptchaError('timeout')),
    })
    if (widgetId === undefined) throw new CaptchaError('turnstile-error')

    this.widgetId = widgetId
    this.renderedAction = action
  }
}
